import React, { useCallback, useEffect, useState } from "react";
import {
    ResourceVisibility,
    getResourceVisibility,
    updateResourceVisibility,
} from "../services/resourceVisibilityService";
import { enforceSessionOnAuthError } from "../utils/sessionGuard";
import "../styles/resource-display-config.css";

/** Extracts the HTTP status from a service error, if present. */
function errorStatus(error: unknown): number | undefined {
    return typeof error === "object" && error !== null && "status" in error
        ? (error as { status?: number }).status
        : undefined;
}

type ToggleKey = keyof ResourceVisibility;

interface RowConfig {
    key: ToggleKey;
    label: string;
    testId: string;
}

const ROWS: RowConfig[] = [
    {
        key: "collegeResourcesEnabled",
        label: "Polaris Athlete college counseling courses & resources",
        testId: "toggle-college-resources",
    },
    {
        key: "recruitingResourcesEnabled",
        label: "Polaris Athlete recruiting courses & resources",
        testId: "toggle-recruiting-resources",
    },
    {
        key: "polarisHubVideosEnabled",
        label: "Polaris Hub videos (not your school’s own Hub videos)",
        testId: "toggle-polaris-hub-videos",
    },
    {
        key: "polarisHubResourcesEnabled",
        label: "Polaris Hub resources (not your school’s own Hub resources)",
        testId: "toggle-polaris-hub-resources",
    },
];

/**
 * Admin/staff panel (rendered on the Hub page) that controls which Polaris Athlete
 * resources a school's student-athletes can see. Toggling a row off immediately
 * persists the change; the athlete UI and backend then hide the matching content.
 */
export default function ResourceDisplayConfig() {
    const [visibility, setVisibility] = useState<ResourceVisibility | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savingKey, setSavingKey] = useState<ToggleKey | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setVisibility(await getResourceVisibility());
        } catch (e) {
            const status = errorStatus(e);
            // A swapped/expired session (e.g. another account signed in via a shared
            // incognito cookie jar) surfaces here as 401/403. Force a clean re-login
            // rather than showing a generic "unable to load" message.
            if (status && (await enforceSessionOnAuthError(status))) {
                return;
            }
            setLoadError("Unable to load resource display configurations.");
            setVisibility(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleToggle = useCallback(
        async (key: ToggleKey) => {
            if (!visibility || savingKey) {
                return;
            }
            const next: ResourceVisibility = { ...visibility, [key]: !visibility[key] };
            setSavingKey(key);
            setSaveError(null);
            setVisibility(next); // optimistic
            try {
                const saved = await updateResourceVisibility(next);
                setVisibility(saved);
            } catch (e) {
                setVisibility(visibility); // revert
                const status = errorStatus(e);
                // If the session was swapped/expired (401/403), send the user to a
                // clean re-login instead of the generic "unable to save" message.
                if (status && (await enforceSessionOnAuthError(status))) {
                    return;
                }
                setSaveError("Unable to save changes. Please try again.");
            } finally {
                setSavingKey(null);
            }
        },
        [visibility, savingKey]
    );

    return (
        <section
            className="resource-display-config"
            aria-label="Resource Display Configurations"
            data-testid="resource-display-config"
        >
            <div className="rdc-head">
                <h2 className="rdc-title">Resource Display Configurations</h2>
                <p className="rdc-subtitle">Control what resources your student-athletes see.</p>
            </div>

            {loading && <div className="rdc-loading">Loading…</div>}

            {!loading && loadError && (
                <div className="rdc-error" role="alert">
                    {loadError}
                </div>
            )}

            {!loading && !loadError && visibility && (
                <div className="rdc-table" role="table">
                    <div className="rdc-row rdc-row--header" role="row">
                        <span className="rdc-col rdc-col--resource" role="columnheader">
                            Resources
                        </span>
                        <span className="rdc-col rdc-col--access" role="columnheader">
                            Access
                        </span>
                        <span className="rdc-col rdc-col--toggle" role="columnheader" aria-hidden="true" />
                    </div>

                    {ROWS.map((row) => {
                        const checked = visibility[row.key];
                        return (
                            <div className="rdc-row" role="row" key={row.key}>
                                <span className="rdc-col rdc-col--resource" role="cell">
                                    {row.label}
                                </span>
                                <span className="rdc-col rdc-col--access" role="cell">
                                    Student-athlete
                                </span>
                                <span className="rdc-col rdc-col--toggle" role="cell">
                                    <label className="rdc-switch">
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            checked={checked}
                                            disabled={savingKey !== null}
                                            aria-label={row.label}
                                            data-testid={row.testId}
                                            onChange={() => handleToggle(row.key)}
                                        />
                                        <span className="rdc-slider" aria-hidden="true" />
                                    </label>
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {saveError && (
                <div className="rdc-error" role="alert">
                    {saveError}
                </div>
            )}
        </section>
    );
}
